<template>
  <scrollable-sidesheet :active="active" :title="title" @close="close">
    <m-confirm ref="confirm" mode="danger" />

    <template v-if="reportId" #header-actions>
      <c-button size="small" :icon="{ icon: 'trash', color: 'secondary' }" label="Удалить" @click="remove" />
    </template>

    <c-loader v-if="loading" :active="loading" label="Данные загружаются" :height="300" />

    <div
      class="report"
      :style="{
        display: loading ? 'none' : '',
      }"
    >
      <div class="row">
        <div class="col col-12 col-md-10 col-xl-6">
          <div class="form-group report__name">
            <c-input
              v-model="form.name"
              floating-label="Наименование отчета"
              :required-mark="true"
              :clearable="true"
              :max-length="255"
              :required="isSubmitted && nameError"
              :required-condition="isSubmitted && nameError"
              :class="{ 'is-invalid': isSubmitted && nameError }"
            />
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col col-12 col-md-5 col-xl-3">
          <div class="form-group report__category">
            <c-select
              v-model="form.category"
              type="select"
              floating-label="Категория"
              :clearable="true"
              :searchable="true"
              :items="dictionaries.categories"
            />
          </div>
        </div>
        <div class="col col-12 col-md-5 col-xl-3">
          <div class="form-group report__responsible">
            <c-select
              ref="responsible"
              v-model="form.responsible"
              type="select"
              floating-label="Ответственный"
              :clearable="true"
              :searchable="true"
              :items="dictionaries.workers"
            />
          </div>
        </div>
      </div>

      <div class="form-group report__chat">
        <c-switcher v-model="form.chat" label="Включить обсуждение" />
      </div>

      <div class="row">
        <div class="col col-12 col-md-10 col-xl-9">
          <div class="form-group report__description">
            <c-textarea
              ref="description"
              v-model="form.description"
              class="description"
              floating-label="Описание отчета"
              :clearable="true"
            />
          </div>
        </div>
      </div>

      <div class="report__files">
        <file-loader
          ref="loader"
          v-model="form.files"
          :limit="10"
          format="all"
          actions="loadFiles,preview"
          :links="form.fileLinks"
          @changeLinks="form.fileLinks = $event"
          :accept="'jpeg,jpg,gif,png,mp4,avi,wmv,mov,3gp,flv,mpeg-1,mpeg-2,webm'"
        >
          <template #note>
            <div class="note">
              Допустимые форматы изображений: .jpeg, .jpg, .gif, .png, видео: .mp4, .avi, .wmv, .mov, .3gp, .flv,
              .mpeg-1,2, .webm. Размер файла — не более 10Мб.
            </div>
          </template>
        </file-loader>
      </div>
    </div>

    <template #actions>
      <c-button size="large" label="Отменить" @click="close" />
      <c-button :label="submitLabel" color="primary" size="large" :disabled="pending" @click="submit" />
    </template>
  </scrollable-sidesheet>
</template>

<script>
import ScrollableSidesheet from "@/components/scrollable-sidesheet";
import CButton from "@/components/controls2/Button";
import CInput from "@/components/controls2/Input";
import CSelect from "@/components/controls2/Select";
import CTextarea from "@/components/controls/textarea";
import CSwitcher from "@/components/controls2/Switcher";
import CLoader from "@/components/loader";
import FileLoader from "@/components/file-loader";
import MConfirm from "@/components/modals/confirm-modal/index";

import { REQUIRED_ERROR } from "@/constants/errorFieldValues";

import { formatOlapReport, OlapReport } from "../models/olap-report";
import {
  CREATE_REPORT_ACTION,
  EDIT_REPORT_ACTION,
  GET_CATEGORIES_ACTION,
  GET_REPORT_ACTION,
  GET_WORKERS_ACTION,
  REMOVE_REPORT_ACTION,
} from "../utils/actions";
import { NEW_REPORT_TITLES } from '../utils/codes';

export default {
  components: { ScrollableSidesheet, CButton, CInput, CSelect, CSwitcher, CTextarea, CLoader, FileLoader, MConfirm },
  props: ["code"],
  data() {
    return {
      active: false,

      reportId: null,
      report: null,
      loading: false,

      isSubmitted: false,
      pending: false,

      dictionaries: {
        categories: [],
        workers: [],
      },

      form: {
        name: "",
        category: null,
        responsible: null,
        chat: false,
        description: "",
        files: [],
        fileLinks: [],
      },
    };
  },

  computed: {
    title() {
      return this.reportId ? "Редактирование отчета" : NEW_REPORT_TITLES[this.code] || "Новый отчет";
    },
    submitLabel() {
      return this.reportId ? "Сохранить" : "Сохранить и перейти к настройке";
    },
    nameError() {
      return this.form.name.trim() ? null : REQUIRED_ERROR;
    },
    isValid() {
      return !this.nameError;
    },
  },

  async created() {
    await Promise.all([this.loadWorkers()]);
  },

  methods: {
    loadCategories() {
      console.log("load categories");
      return this.$store.dispatch(GET_CATEGORIES_ACTION, { code: this.code }).then((result) => {
        this.dictionaries.categories = result.map((category) => ({ value: category.id, label: category.name }));
      });
    },
    loadWorkers() {
      return this.$store.dispatch(GET_WORKERS_ACTION).then((result) => {
        this.dictionaries.workers = result.map((worker) => ({ value: worker.id, label: worker.name }));
      });
    },
    async open(reportId, focus) {
      this.loading = true;
      const categoriesPromise = this.loadCategories();

      if (reportId) {
        this.reportId = reportId;

        await this.loadReportData(reportId).then(() => {
          if (focus) {
            this.$nextTick(() => {
              if (focus === "responsible") {
                this.$refs.responsible.focus();
              } else if (focus === "description") {
                this.$refs.description.focus();
              }
            });
          }
        });
      }
      this.active = true;
      await categoriesPromise;
      this.loading = false;
    },

    close() {
      this.active = false;
      this.reset();
    },

    loadReportData(reportId) {
      return this.$store.dispatch(GET_REPORT_ACTION, { id: reportId, code: this.code }).then((result) => {
        this.setData(result);
      });
    },

    setData(data) {
      const model = OlapReport(data);
      this.report = model;

      const { name, category, responsible, chat, description, files } = model;
      this.form.name = name || "";
      this.form.category = category && { value: category };
      this.form.responsible = responsible && { value: responsible.id };
      this.form.chat = chat;
      this.form.description = description;
      this.form.fileLinks = files;
    },

    getParams() {
      const { name, category, responsible, chat, files, fileLinks, description } = this.form;

      let oldFiles = null;

      if (this.report && this.report.files.length) {
        oldFiles = fileLinks.map(l => l.id);
      }

      const params = formatOlapReport({
        id: this.reportId,
        name,
        category: category && category.value,
        responsible: responsible && responsible.value,
        chat,
        files,
        oldFiles,
        description,
      });

      return {
        ...params,
        code: this.code,
      };
    },

    reset() {
      this.isSubmitted = false;
      this.pending = false;

      this.reportId = null;
      this.form.name = "";
      this.form.category = null;
      this.form.responsible = null;
      this.form.chat = false;
      this.form.description = "";

      this.$refs.loader.clear();
    },

    remove() {
      this.$refs.confirm.open({
        title: "Удалить отчет",
        text: `Отчёт <b>${this.report.name}</b> будет удален без возможности восстановления`,
        confirm: "Удалить",
        onConfirm: () => {
          this.$store.dispatch(REMOVE_REPORT_ACTION, { reportId: this.reportId }).then(() => {
            this.$emit("removed", this.report);
            this.close();
          });
        },
      });
    },

    submit() {
      this.isSubmitted = true;
      if (!this.isValid) return;
      this.pending = true;

      const action = this.reportId ? EDIT_REPORT_ACTION : CREATE_REPORT_ACTION;

      this.$store.dispatch(action, this.getParams()).then((response) => {
        this.$emit(this.reportId ? "updated" : "created", response.model);
        this.close();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";
@import "~@/assets/styles/grid";

.report {
  .note {
    font-size: 11px;
    display: flex;
    align-items: center;
    min-height: 30px;
    padding-top: 20px;
    color: $color-label;
  }

  @include bootstrapSm {
    @include gridGenerator(11);

    .note {
      padding-top: 0;
      padding-left: 30px;
    }
  }
}
</style>
